import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// ADDED: Import package management functions
import { 
  validateScanAvailability, 
  incrementScanUsage 
} from '../utils/packageManagement';

import './TeamCreation.css';

const TeamCreation = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  // Main state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Team data
  const [teamData, setTeamData] = useState({
    name: '',
    description: ''
  });
  
  // Members
  const [members, setMembers] = useState(['']);
  const [createdTeam, setCreatedTeam] = useState(null);
  const [emailStatus, setEmailStatus] = useState('pending');

  // ADDED: Package validation state
  const [packageValidation, setPackageValidation] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);

  // ADDED: Load package validation on component mount
  useEffect(() => {
    if (user?.email) {
      loadPackageValidation();
    }
  }, [user]);

  // ADDED: Package validation loader
  const loadPackageValidation = async () => {
    try {
      setPackageLoading(true);
      const validation = await validateScanAvailability(user.email);
      setPackageValidation(validation);
      console.log('📦 Package validation loaded:', validation);
    } catch (error) {
      console.error('Error loading package validation:', error);
      setPackageValidation({
        canScan: false,
        message: 'Fout bij laden scan status',
        scansLeft: 0
      });
    } finally {
      setPackageLoading(false);
    }
  };

  // Form validation
  const validateStep1 = () => {
    if (!teamData.name.trim()) {
      setError('Relatienaam is verplicht');
      return false;
    }
    if (teamData.name.trim().length < 2) {
      setError('Relatienaam moet minimaal 2 karakters lang zijn');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    const validEmails = members.filter(email => email.trim() && isValidEmail(email.trim()));
    if (validEmails.length === 0) {
      setError('Voeg minimaal één geldig emailadres toe');
      return false;
    }
    setError('');
    return true;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const generateInviteCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  // Step navigation
  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      submitTeam();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Member management
  const addMember = () => {
    setMembers([...members, '']);
  };

  const updateMember = (index, value) => {
    const newMembers = [...members];
    newMembers[index] = value;
    setMembers(newMembers);
  };

  const removeMember = (index) => {
    if (members.length > 1) {
      const newMembers = members.filter((_, i) => i !== index);
      setMembers(newMembers);
    }
  };

  // UPDATED: Team submission with package management
  const submitTeam = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // ADDED: Pre-flight package validation
      console.log('📦 Validating package availability...');
      const currentValidation = await validateScanAvailability(user.email);
      
      if (!currentValidation.canScan) {
        setError(currentValidation.message);
        setIsSubmitting(false);
        return;
      }

      // Filter valid emails
      const validEmails = members.filter(email => email.trim() && isValidEmail(email.trim()));
      
      console.log('Creating team with organization filtering...');
      console.log('Current user profile:', userProfile);
      console.log('Valid emails:', validEmails);

      const organizationId = userProfile?.organizationId || 'default';

      // Create team document
      const teamDoc = {
        name: teamData.name.trim(),
        description: teamData.description.trim(),
        createdBy: user.displayName || user.email,
        createdByEmail: user.email,
        organizationId: organizationId,
        createdAt: serverTimestamp(),
        status: 'active',
        memberCount: validEmails.length
      };

      console.log('Creating team with data:', teamDoc);
      const docRef = await addDoc(collection(db, 'teams'), teamDoc);
      console.log('Team created with ID:', docRef.id);

      // Create invitations in subcollection + index
      const invitations = [];

      for (const email of validEmails) {
        const inviteCode = generateInviteCode();
        
        // Add to teams/{teamId}/invites subcollection
        const inviteRef = await addDoc(collection(db, 'teams', docRef.id, 'invites'), {
          email: email.trim(),
          code: inviteCode,
          used: false,
          createdAt: serverTimestamp()
        });

        // Add to index for fast lookup
        await addDoc(collection(db, 'invitations_index'), {
          code: inviteCode,
          teamId: docRef.id,
          inviteId: inviteRef.id,
          email: email.trim(),
          createdAt: serverTimestamp()
        });

        invitations.push({
          email: email.trim(),
          code: inviteCode,
          id: inviteRef.id
        });

        console.log('✅ Invitation created in subcollection + index:', { 
          email: email.trim(), 
          code: inviteCode,
          teamId: docRef.id 
        });
      }

      console.log('All invitations created:', invitations);

      // ADDED: Increment package usage after successful team creation
      try {
        console.log('📦 Incrementing package usage...');
        const usageResult = await incrementScanUsage(user.email);
        
        if (usageResult.success) {
          console.log('✅ Package usage incremented:', usageResult.message);
        } else {
          console.error('❌ Package usage increment failed:', usageResult.message);
          // Note: Team is already created, so we log but don't fail
        }
      } catch (packageError) {
        console.error('❌ Error incrementing package usage:', packageError);
        // Team creation succeeded, package update failed - continue with flow
      }

      // Create email job via Firestore trigger
      try {
        console.log('Creating email job via Firestore trigger...');
        
        const emailJobData = {
          teamId: docRef.id,
          invites: invitations.map(inv => ({
            email: inv.email,
            code: inv.code
          })),
          teamInfo: {
            teamName: teamData.name,
            createdBy: user.displayName || user.email,
            organizationId: organizationId
          },
          status: 'pending',
          createdAt: serverTimestamp(),
          createdBy: user.email
        };

        console.log('Email job data:', emailJobData);

        // Create email job document (triggers Firebase Function)
        const emailJobRef = await addDoc(collection(db, 'email_jobs'), emailJobData);
        console.log('Email job created:', emailJobRef.id);

        setEmailStatus('success');
      } catch (emailError) {
        console.log('Email job creation failed, but team created successfully:', emailError);
        setEmailStatus('warning');
      }

      console.log('Team creation completed successfully');
      
      setCreatedTeam({
        id: docRef.id,
        name: teamData.name,
        memberCount: validEmails.length
      });

      setCurrentStep(3); // Success step
      
      // Dispatch event for Dashboard to refresh
      window.dispatchEvent(new CustomEvent('teamCreated'));
      
    } catch (error) {
      console.error('Error creating team:', error);
      setError('Er is een fout opgetreden bij het aanmaken van de relatie. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ADDED: Package status indicator component
  const renderPackageStatus = () => {
    if (packageLoading) {
      return (
        <div className="package-status-bar package-status-loading">
          <span className="loading-spinner"></span>
          <span>Package status laden...</span>
        </div>
      );
    }

    if (!packageValidation?.canScan) {
      return (
        <div className="package-status-bar package-status-error">
          <span className="package-icon">⚠️</span>
          <div className="package-text">
            <strong>Geen scans beschikbaar</strong>
            <p>{packageValidation?.message || 'Neem contact op met diederik@tractum.nl'}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="package-status-bar package-status-success">
        <span className="package-icon">✅</span>
        <div className="package-text">
          <strong>{packageValidation.scansLeft} scan(s) beschikbaar</strong>
          <p>Na aanmaken blijven er {packageValidation.scansLeft - 1} over</p>
        </div>
      </div>
    );
  };

  return (
    <div className="team-creation-container">
      <div className="team-creation-card">
        <div className="team-creation-header">
          <h1> Relatie Aanmaken </h1>
          <div className="progress-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <span className="step-number"> 1 </span>
              <span className="step-label"> Relatie Info </span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <span className="step-number"> 2 </span>
              <span className="step-label"> Cliënten </span>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <span className="step-number"> 3 </span>
              <span className="step-label"> Voltooid </span>
            </div>
          </div>
        </div>

        {/* ADDED: Package Status Bar */}
        {renderPackageStatus()}

        <div className="team-creation-content">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Step 1: Team Information */}
          {currentStep === 1 && (
            <div className="form-step">
              <h2>Relatie Informatie</h2>
              <div className="form-group">
                <label htmlFor="teamName">Relatienaam *</label>
                <input
                  id="teamName"
                  type="text"
                  value={teamData.name}
                  onChange={(e) => setTeamData({ ...teamData, name: e.target.value })}
                  placeholder="Voer de relatienaam in, minimaal 2 karakters"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="teamDescription">Beschrijving (optioneel)</label>
                <textarea
                  id="teamDescription"
                  value={teamData.description}
                  onChange={(e) => setTeamData({ ...teamData, description: e.target.value })}
                  placeholder="Voer een korte beschrijving in, bv de datum zodat je later makkelijk twee RelatieScans kunt vergelijken"
                  className="form-textarea"
                  rows={3}
                />
              </div>
              <div className="button-group">
                <button 
                  onClick={nextStep} 
                  className="btn-primary"
                  disabled={!packageValidation?.canScan}
                >
                  Volgende
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Team Members */}
          {currentStep === 2 && (
            <div className="form-step">
              <h2>Cliënten uitnodigen</h2>
              <p className="step-description">
                Voeg de emailadressen van je cliënten toe. Zij ontvangen een uitnodiging om deel te nemen aan de RelatieScan.
              </p>
              
              <div className="members-list">
                {members.map((member, index) => (
                  <div key={index} className="member-input-group">
                    <input
                      type="email"
                      value={member}
                      onChange={(e) => updateMember(index, e.target.value)}
                      placeholder="cliënt@mail.nl"
                      className="form-input member-input"
                    />
                    {members.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMember(index)}
                        className="btn-remove"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addMember}
                className="btn-secondary add-member-btn"
              >
                + Nog een cliënt toevoegen
              </button>

              <div className="button-group">
                <button onClick={prevStep} className="btn-secondary">
                  Vorige
                </button>
                <button 
                  onClick={nextStep} 
                  className="btn-primary"
                  disabled={isSubmitting || !packageValidation?.canScan}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Relatie Aanmaken...
                    </>
                  ) : (
                    'Relatie Aanmaken'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="form-step success-step">
              <div className="success-container">
                <div className="success-icon">✅</div>
                <h2>Relatie succesvol aangemaakt!</h2>
                
                <div className="success-details">
                  <p><strong>Relatie:</strong> {createdTeam?.name}</p>
                  <p><strong>Cliënten:</strong> {createdTeam?.memberCount}</p>
                  <p><strong>Status:</strong> {
                    emailStatus === 'success' ? '✅ Uitnodigingen verzonden!' :
                    emailStatus === 'warning' ? '⚠️ Relatie aangemaakt, emails volgen later' :
                    '⏳ Emails worden verwerkt...'
                  }</p>
                </div>

                <div className="button-group">
                  <button 
                    onClick={() => navigate('/dashboard')}
                    className="btn-primary"
                  >
                    Naar Dashboard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCreation;